import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiLayers,
  FiShield,
  FiActivity,
  FiEye,
  FiLock,
  FiRefreshCw,
  FiSmartphone,
  FiDatabase,
  FiCheck,
  FiX,
  FiGitBranch,
  FiCompass,
  FiTrendingUp,
} from 'react-icons/fi';
import { Reveal, SectionHeading, AnimatedCounter } from '../components/ui';
import { moduleGroups, moduleCount } from '../data/product';

/* ------------------------------------------------------------------ *
 * Hero
 * ------------------------------------------------------------------ */

const Hero: React.FC = () => (
  <section className="relative overflow-hidden bg-ink-900 pb-24 pt-32 sm:pt-40">
    <div className="absolute inset-0 blueprint-grid mask-fade opacity-70" aria-hidden />
    <div
      className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-primary-600/25 blur-3xl animate-blob"
      aria-hidden
    />
    <div
      className="absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl animate-blob-delayed"
      aria-hidden
    />

    <div className="container-custom relative">
      <div className="max-w-3xl">
        <Reveal>
          <span className="pill-invert mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
            About BuildFlow
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl">
            Construction runs on spreadsheets.{' '}
            <span className="text-gradient-animate">We think it deserves better.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl lede-invert">
            Architecture and construction firms in India were handed a choice between design tools
            that don't understand money and accounting software that doesn't understand projects.
            BuildFlow is the third option — {moduleCount} connected modules that treat a project,
            its crew, its suppliers and its cash as one thing, because on site they already are.
          </p>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">
              Talk to us
              <FiArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" className="btn-ghost-invert">
              See pricing
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Why we exist — the before / after
 * ------------------------------------------------------------------ */

const BEFORE = [
  'Attendance in a site register, payroll in a different sheet',
  'Purchase orders on WhatsApp, bills in a drawer',
  'Project spend that nobody can total until month end',
  'Three versions of the truth, none of them current',
];

const AFTER = [
  'Attendance marked on a phone, flowing straight to payroll',
  'POs raised in the system, vendor bills created on receipt',
  'Spend per project visible the moment a cost is booked',
  'One record, one number, with a timeline showing how it got there',
];

const Story: React.FC = () => (
  <section className="section">
    <div className="container-custom">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <SectionHeading
            align="left"
            eyebrow="Why we exist"
            title="The work is joined up. The software never was."
          />
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-600">
            <Reveal delay={0.12}>
              <p>
                Talk to anyone running a practice and the same picture appears: drawings are
                immaculate, and everything behind them is held together by spreadsheets, group chats
                and one person who remembers everything. The information exists — it just never sits
                anywhere it can be added up.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p>
                So decisions get made on stale numbers. A project looks profitable until the vendor
                bills arrive. A worker's attendance is argued over because two registers disagree.
                Nobody is careless; the tools simply never connected the pieces.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-medium text-ink-700">
                BuildFlow was built the other way round — start with the money and the project, then
                make every module post into them. Nothing is a standalone form. Every screen either
                feeds the ledger or reads from it.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Before / after */}
        <Reveal delay={0.1}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                  <FiX className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Before
                </h3>
              </div>
              <ul className="space-y-3">
                {BEFORE.map((b) => (
                  <li key={b} className="text-sm leading-relaxed text-slate-500">
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative overflow-hidden rounded-2xl border-2 border-primary-200 bg-white p-5 shadow-card">
              <div
                className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-600 to-accent-500"
                aria-hidden
              />
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <FiCheck className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-primary-600">
                  With BuildFlow
                </h3>
              </div>
              <ul className="space-y-3">
                {AFTER.map((a) => (
                  <li key={a} className="text-sm font-medium leading-relaxed text-ink-700">
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Principles
 * ------------------------------------------------------------------ */

const PRINCIPLES = [
  {
    icon: FiActivity,
    title: 'A number appears once',
    desc:
      'Totals are computed where the data lives, never re-typed into a second place. If project spend and the vendor ledger could ever disagree, we treat that as a bug, not a reconciliation task for you.',
  },
  {
    icon: FiEye,
    title: 'Nothing is a dead end',
    desc:
      'Open a vendor and you see every order and rupee spent. Open a worker and you see attendance, projects and payments. A record that tells you only what you already typed in isn\'t worth a screen.',
  },
  {
    icon: FiShield,
    title: 'Every change is on the record',
    desc:
      'Field-level history on every module, not a vague "edited" flag. When a figure looks wrong at the end of the quarter, you should be able to see exactly how it got that way.',
  },
  {
    icon: FiLock,
    title: 'Your data is yours, and separate',
    desc:
      'Each firm is provisioned its own database schema rather than sharing tables. Export any register or report to PDF or Excel whenever you like — including on the way out.',
  },
];

const Principles: React.FC = () => (
  <section className="relative overflow-hidden bg-ink-900 py-20 sm:py-28">
    <div className="absolute inset-0 blueprint-grid opacity-50" aria-hidden />
    <div className="container-custom relative">
      <SectionHeading
        invert
        eyebrow="What we believe"
        title="Four rules we don't bend"
        sub="These aren't slogans — each one shows up as a specific behaviour in the product."
      />

      <div className="mt-14 grid gap-5 sm:grid-cols-2">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.07}>
            <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:border-accent-400/40 hover:bg-white/[0.07]">
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-accent-500 text-white shadow-glow transition-transform duration-300 group-hover:scale-110">
                <p.icon className="h-5 w-5" />
              </span>
              <h3 className="font-display text-lg font-semibold text-white">{p.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * How we build
 * ------------------------------------------------------------------ */

const HOW = [
  {
    icon: FiGitBranch,
    title: 'End to end, or not at all',
    desc:
      'A module ships when the list, the detail view, the create and edit forms, the validation, the permissions and the reports all work. We would rather have fewer modules that are finished than a long feature list of half-built screens.',
  },
  {
    icon: FiDatabase,
    title: 'Server-side truth',
    desc:
      'Permissions, totals and status rules are enforced on the backend, not just hidden in the interface. What the browser shows is a view of the rules, never the rules themselves.',
  },
  {
    icon: FiSmartphone,
    title: 'Site-first, not desk-only',
    desc:
      'Attendance gets marked standing in the dust, not at a desk. Every screen is built to work on a phone on a weak connection, because that is where half the real data enters the system.',
  },
  {
    icon: FiRefreshCw,
    title: 'Shipped continuously',
    desc:
      'We are a young product and we move fast. New modules and reports arrive for every customer on the same plan they already pay for — you do not buy a version and wait three years for the next one.',
  },
];

const HowWeBuild: React.FC = () => (
  <section className="section">
    <div className="container-custom">
      <SectionHeading
        eyebrow="How we build"
        title="Boring engineering, on purpose"
        sub="Financial software earns trust slowly. These are the habits that keep it."
      />

      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {HOW.map((h, i) => (
          <Reveal key={h.title} delay={i * 0.07}>
            <div className="card-interactive h-full p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <h.icon className="h-5 w-5" />
                </span>
                <span className="font-display text-2xl font-bold text-slate-200">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-base font-semibold text-ink-800">{h.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Product facts — real, checkable numbers only
 * ------------------------------------------------------------------ */

/**
 * Explicitly typed so the optional prefix/suffix don't make TypeScript infer a
 * union of two different object shapes, which would then reject `f.prefix`.
 */
const FACTS: {
  value: number;
  label: string;
  note: string;
  prefix?: string;
  suffix?: string;
}[] = [
  { value: moduleCount, label: 'Connected modules', note: 'Every one shipped and in use' },
  { value: moduleGroups.length, label: 'Areas covered', note: 'Sales to finance, end to end' },
  { value: 15, suffix: '+', label: 'Built-in reports', note: 'PDF and Excel export included' },
  { value: 999, prefix: '₹', label: 'Entry price per month', note: 'No per-module add-on fees' },
];

const Facts: React.FC = () => (
  <section className="pb-4">
    <div className="container-custom">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-card sm:p-10">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {FACTS.map((f) => (
              <div key={f.label} className="text-center sm:text-left">
                <div className="font-display text-4xl font-bold text-ink-800">
                  <AnimatedCounter value={f.value} suffix={f.suffix} prefix={f.prefix} />
                </div>
                <p className="mt-1.5 text-sm font-semibold text-ink-700">{f.label}</p>
                <p className="mt-0.5 text-xs text-slate-500">{f.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Where we're going
 * ------------------------------------------------------------------ */

const ROADMAP = [
  {
    stage: 'Shipped',
    icon: FiLayers,
    title: `All ${moduleCount} core modules`,
    desc:
      'Leads and projects through to attendance, procurement, the unified money ledger and the audit trail. The full operating spine of a firm.',
    done: true,
  },
  {
    stage: 'In progress',
    icon: FiCompass,
    title: 'Deeper reporting and forecasting',
    desc:
      'Cash-flow projection from committed spend and outstanding invoices, plus custom report building on top of the existing registers.',
    done: false,
  },
  {
    stage: 'Next',
    icon: FiTrendingUp,
    title: 'The wider ecosystem',
    desc:
      'Client-facing project portals, document and drawing revisions against each project, and an open API so BuildFlow sits alongside the tools you already run.',
    done: false,
  },
];

const Roadmap: React.FC = () => (
  <section className="section">
    <div className="container-custom">
      <SectionHeading
        eyebrow="Where we're going"
        title="Built to grow with your firm"
        sub="We publish what we're working on, because you're choosing a direction as much as a product."
      />

      <div className="relative mx-auto mt-14 max-w-3xl">
        {/* Spine */}
        <div
          className="absolute bottom-6 left-[21px] top-6 w-px bg-gradient-to-b from-primary-500 via-accent-400 to-slate-200"
          aria-hidden
        />
        <div className="space-y-6">
          {ROADMAP.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <div className="relative flex gap-5">
                <span
                  className={`relative z-10 flex h-11 w-11 flex-none items-center justify-center rounded-full border-4 border-white shadow-sm ${
                    r.done
                      ? 'bg-gradient-to-br from-primary-600 to-accent-500 text-white'
                      : 'bg-white ring-1 ring-slate-200 text-primary-600'
                  }`}
                >
                  <r.icon className="h-5 w-5" />
                </span>
                <div className="card flex-1 p-5">
                  <span
                    className={`mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      r.done
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-primary-50 text-primary-700'
                    }`}
                  >
                    {r.done && <FiCheck className="h-3 w-3" strokeWidth={3} />}
                    {r.stage}
                  </span>
                  <h3 className="font-display text-lg font-semibold text-ink-800">{r.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{r.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Closing CTA
 * ------------------------------------------------------------------ */

const Closing: React.FC = () => (
  <section className="section pt-0">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-700 via-primary-600 to-accent-600 px-6 py-14 text-center shadow-glow sm:px-12 sm:py-16">
        <div className="absolute inset-0 blueprint-grid-fine opacity-40" aria-hidden />
        <motion.div
          className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden
        />
        <div className="relative">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Come and break it
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-50">
            We'll set up a workspace with your own projects in it. Push it, question the numbers,
            and tell us what your firm actually needs — that feedback is how this gets built.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 shadow-lg transition-transform hover:scale-[1.03] sm:text-base"
            >
              Book a demo
              <FiArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/#platform" className="btn-ghost-invert">
              Explore the modules
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */

export const About: React.FC = () => (
  <div className="overflow-hidden">
    <Hero />
    <Story />
    <Facts />
    <Principles />
    <HowWeBuild />
    <Roadmap />
    <Closing />
  </div>
);

export default About;
